import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import * as dateFns from 'date-fns'
import {
  deleteSignedDoc,
  getCompletedBundleDownload,
  getSignedDocs,
  getSignedFileBlob,
  queryBundleDownloadProgress,
  startBundleDownload,
  updateSignedProcessed
} from '../../actions/document'
import { getUnsignedDocs, getVpsExportData } from '../../actions/admin'
import { saveAs } from 'file-saver';
import AsyncButton from '../util/AsyncButton'
import DatePicker from '../util/DatePicker'
import { Table, Typography, Space, Row, Col, Button, Modal, Checkbox, Input, Radio, Alert, List, message } from 'antd'
import { CloudDownloadOutlined, ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons'
const { Title, Text } = Typography
const { Column } = Table
const { RangePicker } = DatePicker

// "DDMMYYYY-HHMMam" in Norwegian time, e.g. "0508 2026" -> "05082026-0607pm".
const vpsFilenameStamp = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Oslo',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc }, {})
  const hour = (parts.hour === '24' ? '00' : parts.hour).padStart(2, '0')
  return `${parts.day}${parts.month}${parts.year}-${hour}${parts.minute}${parts.dayPeriod.toLowerCase()}`
}

const sanitizeFilenamePart = (value) =>
  (value || 'deal').replace(/\.[^/.]+$/, '').replace(/[<>:"/\\|?*]+/g, '_').trim() || 'deal'

// Render the signing time as "YYYY-MM-DD HH:MM" in Norwegian time (sv-SE gives
// the ISO-style format), matching the timezone used in the signing notification.
const signedAtFormatter = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Oslo',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false
})
const formatSignedAt = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : signedAtFormatter.format(d)
}

const Document = () => {
  const dispatch = useDispatch()
  const signedDocs = useSelector(state => state.data.signedDocs)
  const unsignedDocs = useSelector(state => state.data.unsignedDocs)
  const { hash } = useParams()
  const [ bundleProgress, setBundleProgress ] = useState(-1)
  const [ bundleModalVisible, setBundleModalVisible ] = useState(false)
  const [ bundleRange, setBundleRange ] = useState(null)
  const [ deletingId, setDeletingId ] = useState(null)
  const [ processedUpdatingId, setProcessedUpdatingId ] = useState(null)
  const [ search, setSearch ] = useState('')
  const [ vpsModalVisible, setVpsModalVisible ] = useState(false)
  const [ vpsRange, setVpsRange ] = useState(null)
  const [ vpsProcessedFilter, setVpsProcessedFilter ] = useState('all')
  const [ vpsLoading, setVpsLoading ] = useState(false)
  const [ vpsResult, setVpsResult ] = useState(null)
  const [ vpsErrorMsg, setVpsErrorMsg ] = useState(null)

  const deal = useMemo(() => (unsignedDocs || []).find(d => d._id === hash), [unsignedDocs, hash])

  const refetch = async () => {
    dispatch(getSignedDocs(hash))
  }

  useEffect(() => {
    dispatch(getSignedDocs(hash))
    dispatch(getUnsignedDocs())
  }, [dispatch, hash])

  const decodeText = value => {
    if (value === undefined || value === null) return ''
    const text = String(value)
    if (text.startsWith('\u00FE\u00FF')) {
      // UTF16BE is not supported natively, so we have to manually decode the codepoints
      const charCodes = []
      for (let i = 2; i < text.length; i += 2)
      charCodes.push((text.charCodeAt(i) << 8) | text.charCodeAt(i + 1))
      return String.fromCharCode(...charCodes)
    } else {
      return text
    }
  }

  // Compares two decoded values numerically when both look like numbers,
  // otherwise falls back to a numeric-aware, case-insensitive string compare.
  // This fixes the previous lexicographic sort where e.g. "10" sorted before "9".
  const compareValues = (x, y) => {
    const xs = decodeText(x).trim()
    const ys = decodeText(y).trim()
    if (xs === '' && ys === '') return 0
    if (xs === '') return -1
    if (ys === '') return 1
    const xn = Number(xs)
    const yn = Number(ys)
    if (!Number.isNaN(xn) && !Number.isNaN(yn)) return xn - yn
    return xs.localeCompare(ys, undefined, { numeric: true, sensitivity: 'base' })
  }

  const genericSorter = parent => key => (a, b) =>
    compareValues(a[parent] && a[parent][key], b[parent] && b[parent][key])

  const genericAttributeSorter = genericSorter('attributes')
  const signerDataSorter = genericSorter('signerData')

  const structure = signedDocs && signedDocs.length ? Object.keys(signedDocs[0].attributes).map(key => (
    <Column title={key} dataIndex={['attributes', key]} key={'attr_' + key} render={decodeText} sorter={genericAttributeSorter(key)} />
  )) : ''

  // Free-text search across signer name, all PDF attributes, DB metadata and the file id.
  const filteredDocs = useMemo(() => {
    if (!signedDocs) return signedDocs
    const needle = search.trim().toLowerCase()
    if (!needle) return signedDocs
    return signedDocs.filter(doc => {
      const haystack = [
        doc._id,
        doc.signerData && doc.signerData.displayName,
        formatSignedAt(doc.signedOn),
        ...Object.values(doc.attributes || {}),
        ...Object.values(doc.metadata || {})
      ]
      return haystack.some(v => decodeText(v).toLowerCase().includes(needle))
    })
  }, [signedDocs, search])

  const handleDownloadAll = () => new Promise((resolve, reject) => {
    const start = bundleRange && bundleRange[0] ? dateFns.startOfDay(bundleRange[0]).toISOString() : undefined
    const end = bundleRange && bundleRange[1] ? dateFns.endOfDay(bundleRange[1]).toISOString() : undefined
    startBundleDownload(hash, { start, end }).then(({ jobId }) => {
      setBundleProgress(0)
      const ival = setInterval(() => {
        queryBundleDownloadProgress(jobId).then(data => {
          if (data.state === 'completed') {
            clearInterval(ival)
            setBundleProgress(100)
            getCompletedBundleDownload(jobId)
              .then(data => resolve(data))
              .catch(err => reject(err))
          } else {
            setBundleProgress(data.progress)
          }
        }).catch(console.error)
      }, 1000)
    }).catch(err => reject(err))
  })

  const handleBundleCreated = data => {
    setBundleProgress(-1)
    setBundleModalVisible(false)
    saveAs(data, `${hash}.zip`)
  }

  const handleBundleError = (err) => {
    setBundleProgress(-1)
    const msg = err.response && err.response.data && err.response.data.message
    message.error(msg || 'Could not download PDFs')
  }

  const handleToggleProcessed = async (record, checked) => {
    setProcessedUpdatingId(record._id)
    try {
      await updateSignedProcessed(record._id, checked)
      message.success(checked ? 'Marked as processed' : 'Marked as not processed')
      await refetch()
    } catch (err) {
      message.error('Could not update status')
    } finally {
      setProcessedUpdatingId(null)
    }
  }

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete this signed entry?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <p>
            Signed by <strong>{record.signerData && record.signerData.displayName}</strong>
          </p>
          <p>
            This permanently removes the row from the database. The original signed PDF stays in Signicat's archive, but this app will no longer list it or include it in exports.
          </p>
        </>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeletingId(record._id)
        try {
          await deleteSignedDoc(record._id)
          message.success('Signed entry deleted')
          await refetch()
        } catch (err) {
          message.error('Could not delete signed entry')
        } finally {
          setDeletingId(null)
        }
      }
    })
  }

  const bundleBtnString = bundleProgress < 0 ? 'Download all PDFs' : `Downloading (${bundleProgress}%)`

  const openVpsModal = () => {
    const defaultStart = deal && deal.uploaded ? new Date(deal.uploaded) : null
    setVpsRange([defaultStart, new Date()])
    setVpsProcessedFilter('all')
    setVpsResult(null)
    setVpsErrorMsg(null)
    setVpsModalVisible(true)
  }

  const handleGenerateVps = async () => {
    if (!vpsRange || !vpsRange[0] || !vpsRange[1]) {
      message.error('Pick a start and end date')
      return
    }
    setVpsLoading(true)
    setVpsErrorMsg(null)
    setVpsResult(null)
    try {
      const data = await getVpsExportData(hash, {
        start: dateFns.startOfDay(vpsRange[0]).toISOString(),
        end: dateFns.endOfDay(vpsRange[1]).toISOString(),
        processed: vpsProcessedFilter
      })
      setVpsResult(data)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.message
      setVpsErrorMsg(msg || 'Could not generate the VPS export')
    } finally {
      setVpsLoading(false)
    }
  }

  const buildVpsBlob = (lines) => new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })

  const handleDownloadVpsValid = () => {
    const name = sanitizeFilenamePart(deal && deal.name)
    saveAs(buildVpsBlob(vpsResult.valid.map(e => e.line)), `${name}-${vpsFilenameStamp(new Date())}.txt`)
  }

  const handleDownloadVpsFailed = () => {
    const name = sanitizeFilenamePart(deal && deal.name)
    saveAs(buildVpsBlob(vpsResult.invalid.map(e => e.line)), `${name}-failed-${vpsFilenameStamp(new Date())}.txt`)
  }

  return (
    <div className="stdContent">
      <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
        <Col flex="auto"><Title level={3} style={{ marginBottom: 0 }}>Signed form data</Title></Col>
        <Col flex="320px">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search signed entries"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Col>
        <Col>
          <Button
            type="secondary"
            icon={<CloudDownloadOutlined />}
            onClick={() => { setBundleRange(null); setBundleModalVisible(true) }}
          >
            Download all PDFs
          </Button>
        </Col>
        <Col>
          <Button onClick={openVpsModal}>
            Download VPS file
          </Button>
        </Col>
      </Row>
      <Table
        dataSource={filteredDocs}
        scroll={{ x: 1300 }}
        bordered={true}
        rowKey="_id"
        size="small"
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          showTotal: total => `${total} ${total === 1 ? 'entry' : 'entries'}`
        }}
      >
        <Column title="Signed by" dataIndex={['signerData', 'displayName']} key="_dispName" sorter={signerDataSorter('displayName')} />
        <Column
          title="Signed at"
          key="signedOn"
          width={150}
          sorter={(a, b) => new Date(a.signedOn || 0) - new Date(b.signedOn || 0)}
          render={(text, record) => formatSignedAt(record.signedOn)}
        />
        <Column
          title="Processed"
          key="processed"
          width={120}
          filters={[{ text: 'Processed', value: true }, { text: 'Not processed', value: false }]}
          onFilter={(value, record) => (record.processed === true) === value}
          sorter={(a, b) => Number(a.processed === true) - Number(b.processed === true)}
          render={(text, record) => (
            <Checkbox
              checked={record.processed === true}
              disabled={processedUpdatingId === record._id}
              onChange={(e) => handleToggleProcessed(record, e.target.checked)}
            />
          )}
        />
        {structure}
        <Column
          title="Løpenummer"
          dataIndex="lopenummer"
          key="lopenummer"
          width={110}
          sorter={(a, b) => (a.lopenummer || 0) - (b.lopenummer || 0)}
          render={(value) => value != null ? value : '—'}
        />
        <Column title="File ID" dataIndex="_id" key="_id" />
        <Column
          title="Actions"
          key="actions"
          render={(text, record) => (
            <Space size="middle">
              <Button size="small" type="link" onClick={() => getSignedFileBlob(record._id)
                  .then(data => saveAs(data, `${record._id}.pdf`))
              }>
                Download PDF
              </Button>
              <Button
                size="small"
                type="link"
                danger
                loading={deletingId === record._id}
                onClick={() => handleDelete(record)}
              >
                Delete
              </Button>
            </Space>
          )}
        />
      </Table>

      <Modal
        title="Download all signed PDFs"
        visible={bundleModalVisible}
        onCancel={() => setBundleModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Signed date range</Text>
            <div style={{ marginTop: 8 }}>
              <RangePicker
                value={bundleRange}
                onChange={value => setBundleRange(value)}
                style={{ width: '100%' }}
                allowClear
                placeholder={['All time', 'All time']}
              />
            </div>
          </div>

          <AsyncButton
            type="primary"
            icon={<CloudDownloadOutlined />}
            onClick={handleDownloadAll}
            then={handleBundleCreated}
            catch={handleBundleError}
          >
            {bundleBtnString}
          </AsyncButton>
        </Space>
      </Modal>

      <Modal
        title="Download VPS file"
        visible={vpsModalVisible}
        onCancel={() => setVpsModalVisible(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Signed date range</Text>
            <div style={{ marginTop: 8 }}>
              <RangePicker
                value={vpsRange}
                onChange={value => setVpsRange(value)}
                style={{ width: '100%' }}
                allowClear={false}
              />
            </div>
          </div>

          <div>
            <Text strong>Processed status</Text>
            <div style={{ marginTop: 8 }}>
              <Radio.Group value={vpsProcessedFilter} onChange={e => setVpsProcessedFilter(e.target.value)}>
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="true">Processed only</Radio.Button>
                <Radio.Button value="false">Unprocessed only</Radio.Button>
              </Radio.Group>
            </div>
          </div>

          <Button type="primary" loading={vpsLoading} onClick={handleGenerateVps}>
            Generate
          </Button>

          {vpsErrorMsg && <Alert type="error" showIcon message={vpsErrorMsg} />}

          {vpsResult && (
            <>
              <Alert
                type={vpsResult.invalid.length ? 'warning' : 'success'}
                showIcon
                message={
                  `${vpsResult.valid.length} ${vpsResult.valid.length === 1 ? 'entry is' : 'entries are'} ready to export` +
                  (vpsResult.invalid.length ? `, ${vpsResult.invalid.length} ${vpsResult.invalid.length === 1 ? 'entry has' : 'entries have'} problems` : '')
                }
              />

              {vpsResult.invalid.length > 0 && (
                <List
                  size="small"
                  bordered
                  dataSource={vpsResult.invalid}
                  style={{ maxHeight: 240, overflow: 'auto' }}
                  renderItem={item => (
                    <List.Item>
                      <div>
                        <strong>{item.signedBy || '(unknown signer)'}</strong>
                        <ul style={{ marginBottom: 0 }}>
                          {item.reasons.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    </List.Item>
                  )}
                />
              )}

              <Space>
                <Button type="primary" disabled={vpsResult.valid.length === 0} onClick={handleDownloadVpsValid}>
                  Download valid entries (.txt)
                </Button>
                {vpsResult.invalid.length > 0 && (
                  <Button onClick={handleDownloadVpsFailed}>
                    Download failed entries (.txt)
                  </Button>
                )}
              </Space>
            </>
          )}
        </Space>
      </Modal>
    </div>
  )
}

export default Document
